// Client dossier (expediente de identificación): which data must be captured
// for each client type, which documents must be kept on file, and a printable
// text version. Required once a client has an operation at or above the
// identification threshold (art. 18 fr. I LFPIORPI).
import { formatDate } from "./dates";
import { CLIENT_TYPE_LABELS, ID_DOCUMENT_TYPE_LABELS, RISK_LEVEL_LABELS, RISK_REASON_LABELS } from "./labels";
import { automaticRiskReason, effectiveRiskLevel } from "./risk";
import type { Client, ClientDocument, ClientType, Company, DocumentType, IsoDate } from "./types";

export type DossierField =
  | "name"
  | "rfc"
  | "curp"
  | "birthDate"
  | "incorporationDate"
  | "address"
  | "phone"
  | "country"
  | "idDocumentType"
  | "idDocumentNumber"
  | "legalRepresentative"
  | "beneficialOwner";

export const REQUIRED_FIELDS_BY_CLIENT_TYPE: Record<ClientType, readonly DossierField[]> = {
  individual: ["name", "birthDate", "country", "address", "phone", "rfc", "idDocumentType", "idDocumentNumber"],
  foreign_individual: ["name", "birthDate", "country", "address", "phone", "idDocumentType", "idDocumentNumber"],
  legal_entity: [
    "name",
    "rfc",
    "incorporationDate",
    "country",
    "address",
    "phone",
    "legalRepresentative",
    "beneficialOwner",
  ],
  foreign_legal_entity: [
    "name",
    "incorporationDate",
    "country",
    "address",
    "phone",
    "legalRepresentative",
    "beneficialOwner",
  ],
  trust: ["name", "rfc", "incorporationDate", "address", "legalRepresentative", "beneficialOwner"],
};

/** User-facing (Spanish) labels of the dossier fields. */
export const DOSSIER_FIELD_LABELS: Record<DossierField, string> = {
  name: "Nombre o razón social",
  rfc: "RFC",
  curp: "CURP",
  birthDate: "Fecha de nacimiento",
  incorporationDate: "Fecha de constitución",
  address: "Domicilio",
  phone: "Teléfono",
  country: "País de nacionalidad o residencia",
  idDocumentType: "Tipo de identificación",
  idDocumentNumber: "Número de identificación",
  legalRepresentative: "Representante legal",
  beneficialOwner: "Beneficiario Controlador",
};

export interface DossierDocument {
  /** The kind of file that satisfies this item when uploaded. */
  type: DocumentType;
  /** What the item asks for (user-facing, Spanish). */
  label: string;
}

/** Documents to keep in the client's file, per client type. */
export const DOSSIER_CHECKLIST_BY_CLIENT_TYPE: Record<ClientType, readonly DossierDocument[]> = {
  individual: [
    { type: "id_document", label: "Identificación oficial vigente con fotografía (INE o pasaporte)" },
    { type: "curp", label: "Constancia de la CURP" },
    { type: "tax_certificate", label: "Constancia de situación fiscal (RFC), cuando cuente con ella" },
    { type: "proof_of_address", label: "Comprobante de domicilio no mayor a tres meses" },
    { type: "beneficial_owner_declaration", label: "Declaración sobre la existencia de Beneficiario Controlador" },
  ],
  foreign_individual: [
    { type: "id_document", label: "Pasaporte vigente o documento oficial de identidad" },
    { type: "immigration_document", label: "Documento que acredite su condición de estancia en México, en su caso" },
    { type: "proof_of_address", label: "Comprobante de domicilio de su lugar de residencia" },
    { type: "tax_certificate", label: "Constancia de RFC o CURP, cuando cuente con ellos" },
    { type: "beneficial_owner_declaration", label: "Declaración sobre la existencia de Beneficiario Controlador" },
  ],
  legal_entity: [
    { type: "articles_of_incorporation", label: "Testimonio o copia certificada del instrumento de constitución" },
    { type: "tax_certificate", label: "Constancia de situación fiscal (RFC)" },
    { type: "proof_of_address", label: "Comprobante de domicilio no mayor a tres meses" },
    { type: "power_of_attorney", label: "Instrumento que acredite las facultades del representante legal" },
    { type: "representative_id", label: "Identificación oficial vigente del representante legal" },
    { type: "beneficial_owner_id", label: "Identificación del Beneficiario Controlador (estructura accionaria)" },
  ],
  foreign_legal_entity: [
    { type: "articles_of_incorporation", label: "Documento constitutivo legalizado o apostillado" },
    { type: "tax_certificate", label: "Documento con su número de identificación fiscal del país de origen" },
    { type: "proof_of_address", label: "Comprobante de domicilio" },
    { type: "power_of_attorney", label: "Documento que acredite las facultades del representante legal" },
    { type: "representative_id", label: "Identificación oficial vigente del representante legal" },
    { type: "beneficial_owner_id", label: "Identificación del Beneficiario Controlador" },
  ],
  trust: [
    { type: "trust_agreement", label: "Contrato de fideicomiso (testimonio o copia certificada)" },
    { type: "tax_certificate", label: "Constancia de situación fiscal del fideicomiso (RFC)" },
    { type: "representative_id", label: "Identificación del delegado fiduciario o representante" },
    {
      type: "trust_parties",
      label: "Datos de fideicomitentes, fideicomisarios y, en su caso, del comité técnico",
    },
    { type: "beneficial_owner_id", label: "Identificación del Beneficiario Controlador" },
  ],
};

/** The checklist of a client type with how many files of each kind are already uploaded. */
export function documentChecklist(
  type: ClientType,
  documents: readonly Pick<ClientDocument, "type">[],
): (DossierDocument & { uploaded: number })[] {
  return DOSSIER_CHECKLIST_BY_CLIENT_TYPE[type].map((item) => ({
    ...item,
    uploaded: documents.filter((d) => d.type === item.type).length,
  }));
}

type DossierInput = Pick<Client, "type" | "declaresBeneficialOwner"> & Partial<Pick<Client, DossierField>>;

export function requiredFields(client: DossierInput): DossierField[] {
  const fields = [...REQUIRED_FIELDS_BY_CLIENT_TYPE[client.type]];
  if (client.declaresBeneficialOwner && !fields.includes("beneficialOwner")) {
    fields.push("beneficialOwner");
  }
  return fields;
}

const isBlank = (value: unknown) =>
  value === null || value === undefined || (typeof value === "string" && value.trim() === "");

export const missingFields = (client: DossierInput): DossierField[] =>
  requiredFields(client).filter((field) => isBlank(client[field]));

export const isDossierComplete = (client: DossierInput) => missingFields(client).length === 0;

/** Plain-text dossier (Spanish, user-facing) to copy into the client's file or print. */
export function dossierText(
  client: Client,
  company: Pick<Company, "legalName" | "rfc">,
  generatedOn: IsoDate,
  documents: readonly Pick<ClientDocument, "type">[] = [],
): string {
  const value = (v: string | null | undefined) => (isBlank(v) ? "— (pendiente)" : (v as string));
  const automaticReason = automaticRiskReason(client);
  const risk = RISK_LEVEL_LABELS[effectiveRiskLevel(client)];
  const isPerson = client.type === "individual" || client.type === "foreign_individual";

  const lines = [
    "EXPEDIENTE DE IDENTIFICACIÓN DEL CLIENTE",
    `Sujeto obligado: ${company.legalName}${company.rfc ? ` (RFC ${company.rfc})` : ""}`,
    `Fecha de elaboración: ${formatDate(generatedOn)}`,
    "",
    "DATOS DEL CLIENTE",
    `Tipo de cliente: ${CLIENT_TYPE_LABELS[client.type]}`,
    `${DOSSIER_FIELD_LABELS.name}: ${value(client.name)}`,
    `RFC: ${value(client.rfc)}`,
    ...(isPerson ? [`CURP: ${value(client.curp)}`] : []),
    isPerson
      ? `Fecha de nacimiento: ${client.birthDate ? formatDate(client.birthDate) : value(null)}`
      : `Fecha de constitución: ${client.incorporationDate ? formatDate(client.incorporationDate) : value(null)}`,
    `País: ${value(client.country)}`,
    `Domicilio: ${value(client.address)}`,
    `Teléfono: ${value(client.phone)}`,
  ];
  if (isPerson) {
    const idType = client.idDocumentType ? ID_DOCUMENT_TYPE_LABELS[client.idDocumentType] : null;
    lines.push(`Identificación: ${value(idType)} · Número: ${value(client.idDocumentNumber)}`);
  } else {
    lines.push(`Representante legal: ${value(client.legalRepresentative)}`);
  }
  lines.push(
    `Fecha de alta: ${formatDate(client.registeredOn)}`,
    "",
    "PERFIL DE RIESGO",
    `Grado de Riesgo: ${risk}${automaticReason ? ` (automático: ${RISK_REASON_LABELS[automaticReason]})` : ""}`,
    `Persona Políticamente Expuesta: ${client.pep ? "Sí" : "No"}`,
    `Beneficiario Controlador: ${
      client.beneficialOwner
        ? client.beneficialOwner
        : client.declaresBeneficialOwner
          ? value(null)
          : "Declara que no existe uno distinto del cliente"
    }`,
    "",
    "DOCUMENTOS QUE DEBEN INTEGRARSE AL EXPEDIENTE",
    ...documentChecklist(client.type, documents).map((doc) => `[${doc.uploaded > 0 ? "x" : " "}] ${doc.label}`),
  );

  const missing = missingFields(client);
  if (missing.length > 0) {
    lines.push("", `DATOS PENDIENTES: ${missing.map((f) => DOSSIER_FIELD_LABELS[f]).join(", ")}`);
  }
  return lines.join("\n");
}
